import React from 'react';
import Button from '@atlaskit/button/standard-button';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { Icon, OpenSidebarPayload, useApplicationContext, useSidebar } from '@css-xp/base';
import { validateFreeSubscriptions } from '@css-xp/entitlements-v2';
import { getRouteUrl } from '@css-xp/ppc-utils';
import { routePaths } from '@css-xp/ppc-constants';
import { HelpContent } from '@css-xp/ppc-common';
import { EntitlementDetails, FetchInvoiceGroupDetailsPpcQuery } from '@css-xp/graphtypes/src/generated-types-new';
import { ApolloQueryResult } from '@apollo/client';
import { SIDEBAR } from '../../constants/sidebar';

export const getTitle = (linkedInvoiceGroup: any, isFreeEntitlement: boolean, hasBillToParty: boolean) => {
  return linkedInvoiceGroup || isFreeEntitlement
    ? hasBillToParty
      ? 'Billing profile needs updating first'
      : 'No billing address'
    : 'You need to add a billing profile first';
};

export const renderReasons = (
  linkedInvoiceGroup: any,
  isFreeEntitlement: boolean,
  hasBillToParty: boolean,
  hasDefaultPaymentMethod: boolean
) => (
  <ul>
    {linkedInvoiceGroup ? (
      <>
        {!hasDefaultPaymentMethod && <li>No payment method available for the billing profile</li>}
        {!hasBillToParty && <li>No billing address available for the billing account</li>}
      </>
    ) : (
      <>
        {isFreeEntitlement && !hasBillToParty ? (
          <li>No billing address available for the billing account</li>
        ) : (
          <li>No billing profile available</li>
        )}
      </>
    )}
  </ul>
);

export const getAction = (linkedInvoiceGroup: any, isFreeEntitlement: boolean, hasBillToParty: boolean) => {
  return (
    (!linkedInvoiceGroup && !isFreeEntitlement
      ? 'Switch the billing profile, then try again. Still having trouble? '
      : hasBillToParty
      ? 'Add the information or switch the billing profile, then try again. Still having trouble? '
      : '') + 'For help, contact the'
  );
};
export const ChangeSubscriptionWarningModal = ({
  isOpen,
  onClose,
  payload,
  showSandboxesDecoupled,
}: {
  isOpen: boolean;
  onClose: () => void;
  payload: {
    entitlement: EntitlementDetails;
    invoiceGroupDetails: ApolloQueryResult<FetchInvoiceGroupDetailsPpcQuery | undefined>;
    refetch: () => Promise<ApolloQueryResult<unknown>>;
  };
  showSandboxesDecoupled: boolean;
}) => {
  const { openSidebar } = useSidebar();
  const handleSidebarAction = (openSidebarPayload: OpenSidebarPayload) => {
    openSidebar(openSidebarPayload);
  };

  const partnerData = useApplicationContext();
  const selectedPartnerAccountData = partnerData.partnerAccounts?.find(
    ({ transactionAccountId }: { transactionAccountId: string }) =>
      transactionAccountId === payload.entitlement.transactionAccountId
  );

  const linkedInvoiceGroup = payload.invoiceGroupDetails.data?.invoiceGroup;

  const hasDefaultPaymentMethod = !!linkedInvoiceGroup?.defaultPaymentMethod;
  const isFreeEntitlement = !!validateFreeSubscriptions(payload?.entitlement);
  const hasBillToParty = !isFreeEntitlement && !hasDefaultPaymentMethod ? true : !!selectedPartnerAccountData?.billTo;

  const renderTitle = () => (
    <>
      <Icon type="warning" />
      <span style={{ marginLeft: '24px' }}>{getTitle(linkedInvoiceGroup, isFreeEntitlement, hasBillToParty)}</span>
    </>
  );

  const renderBody = () => (
    <div>
      <p>Subscription changes can't be made because there's:</p>
      <br />
      {renderReasons(linkedInvoiceGroup, isFreeEntitlement, hasBillToParty, hasDefaultPaymentMethod)}
      <br />
      <p>{<HelpContent contentPrefix={getAction(linkedInvoiceGroup, isFreeEntitlement, hasBillToParty)} />}</p>
    </div>
  );

  const renderCloseButton = () => (
    <Button onClick={onClose} appearance="subtle" testId={'close-btn'}>
      Close
    </Button>
  );

  const renderSwitchBillingProfileButton = () => (
    <Button
      onClick={() => {
        onClose();
        handleSidebarAction({
          sidebarType: SIDEBAR.SET_INVOICE_GROUP,
          payload: {
            entitlement: payload.entitlement,
            refetch: payload.refetch,
            isSandboxDecouplingEnabled: showSandboxesDecoupled,
          },
          sidebarOptions: {
            isOverlay: true,
            sidebarWidth: '600px',
          },
        });
      }}
      appearance="primary"
      testId={'switch-billing-profile-btn'}
    >
      Switch billing profile
    </Button>
  );

  const billingProfilePageURL = getRouteUrl(routePaths.billingProfileDetails, {
    txaId: payload.entitlement.transactionAccountId ?? '',
    billingProfileId: `${encodeURIComponent(linkedInvoiceGroup?.id ?? '')}`,
  });

  const renderAddPaymentMethodButton = () => (
    <Button
      onClick={() => {
        onClose();
        window.open(billingProfilePageURL, '_blank');
      }}
      appearance="primary"
      testId={'add-payment-method-btn'}
    >
      Add Payment method
    </Button>
  );

  const renderFooter = () => (
    <>
      {renderCloseButton()}
      {!linkedInvoiceGroup && !isFreeEntitlement
        ? renderSwitchBillingProfileButton()
        : hasBillToParty && renderAddPaymentMethodButton()}
    </>
  );

  return (
    <ModalTransition>
      {isOpen && (
        <Modal onClose={onClose}>
          <ModalHeader>
            <ModalTitle>{renderTitle()}</ModalTitle>
          </ModalHeader>
          <ModalBody>{renderBody()}</ModalBody>
          <ModalFooter>{renderFooter()}</ModalFooter>
        </Modal>
      )}
    </ModalTransition>
  );
};
